import React, { useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { DateField, EmptyState, FormDialog, LoadingState, NumberField } from '../ui';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import {
  createStoreRebateRequest,
  listStoreRebateRequests,
  withdrawStoreRebateRequest,
  type RebateEventRequest,
} from '../../services/rebateRequestsApi';
import { rebateRequestChip } from './rebateRequestShared';

const NS = 'store.eventRebateRequests';
const EMPTY_FORM = {
  brand: '',
  kind: 'event' as 'event' | 'anniversary',
  eventName: '',
  termsKind: 'instant_rebate' as 'instant_rebate' | 'trade_credit',
  amount: '',
  startAt: '',
  endAt: '',
  locationId: '',
  note: '',
};

/**
 * Jordan's side of event rebate requests: ask a brand's vendor to approve an
 * event or anniversary rebate and see its answer. Hidden entirely while
 * `event_rebate_requests` is off for this organization. The vendor decides;
 * the approved rebate appears under Rebate programs above.
 */
export const StoreRebateRequestsSection: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('event_rebate_requests');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const { data: requests, isLoading } = useQuery<RebateEventRequest[]>({
    queryKey: ['store-rebate-requests'],
    queryFn: listStoreRebateRequests,
    enabled,
  });
  const { data: brands } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['brand-registry-names'],
    queryFn: async () => {
      const res = await makeApiCall('/brand-registry', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return ((await res.json()).data || []).map((b: any) => ({ id: b.slug || b.id, name: b.name }));
    },
    enabled: enabled && open,
  });
  const { data: locations } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['store-locations'],
    queryFn: async () => {
      const res = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
    enabled: enabled && open,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['store-rebate-requests'] });
  const create = useMutation({
    mutationFn: () =>
      createStoreRebateRequest({
        brand: form.brand,
        kind: form.kind,
        eventName: form.eventName,
        termsKind: form.termsKind,
        amount: Number(form.amount),
        startAt: new Date(`${form.startAt}T00:00:00`).toISOString(),
        endAt: new Date(`${form.endAt}T23:59:59`).toISOString(),
        scope: form.locationId ? { kind: 'location', locationId: form.locationId } : { kind: 'all' },
        note: form.note || undefined,
      }),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      setForm(EMPTY_FORM);
      refresh();
    },
    onError: (e: any) => setError(e?.message || t(`${NS}.error`)),
  });
  const withdraw = useMutation({
    mutationFn: (id: string) => withdrawStoreRebateRequest(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: any) => setError(e?.message || t(`${NS}.error`)),
  });

  if (!enabled) return null;

  const valid =
    !!form.brand && !!form.eventName.trim() && Number(form.amount) > 0 && !!form.startAt && !!form.endAt && form.endAt >= form.startAt;
  const window = (r: RebateEventRequest) => `${new Date(r.startAt).toLocaleDateString()} – ${new Date(r.endAt).toLocaleDateString()}`;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>{t(`${NS}.title`)}</Typography>
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>{t(`${NS}.action`)}</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {isLoading ? (
        <LoadingState />
      ) : !requests || requests.length === 0 ? (
        <EmptyState title={t(`${NS}.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {requests.map((r) => (
              <Box key={r.id} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">{r.eventName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.brandName} · {t(`${NS}.kind.${r.kind}`)} · {formatCurrency(r.amount)} · {window(r)}
                    </Typography>
                  </Box>
                  {rebateRequestChip(r.status, t, NS)}
                </Stack>
                {r.status === 'denied' && r.decisionNote && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t(`${NS}.vendorNote`, { note: r.decisionNote })}
                  </Typography>
                )}
                {r.status === 'pending' && (
                  <Button size="small" color="inherit" sx={{ mt: 1 }} disabled={withdraw.isPending} onClick={() => withdraw.mutate(r.id)}>
                    {t(`${NS}.withdraw`)}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <FormDialog
        open={open}
        title={t(`${NS}.dialogTitle`)}
        onClose={() => setOpen(false)}
        onSubmit={() => create.mutate()}
        submitLabel={t(`${NS}.submit`)}
        submitDisabled={!valid}
        loading={create.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t(`${NS}.dialogIntro`)}</Typography>
          <TextField select label={t(`${NS}.brand`)} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} fullWidth>
            {(brands || []).map((b) => <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>)}
          </TextField>
          <TextField select label={t(`${NS}.kindLabel`)} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'event' | 'anniversary' })} fullWidth>
            <MenuItem value="event">{t(`${NS}.kind.event`)}</MenuItem>
            <MenuItem value="anniversary">{t(`${NS}.kind.anniversary`)}</MenuItem>
          </TextField>
          <TextField label={t(`${NS}.eventName`)} value={form.eventName} onChange={(e) => setForm({ ...form, eventName: e.target.value })} fullWidth />
          <TextField select label={t(`${NS}.termsKind`)} value={form.termsKind} onChange={(e) => setForm({ ...form, termsKind: e.target.value as 'instant_rebate' | 'trade_credit' })} fullWidth>
            <MenuItem value="instant_rebate">{t(`${NS}.terms.instant_rebate`)}</MenuItem>
            <MenuItem value="trade_credit">{t(`${NS}.terms.trade_credit`)}</MenuItem>
          </TextField>
          <NumberField label={t(`${NS}.amount`)} adornment="$" min={0} step="0.01" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} sx={{ width: '100%' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DateField label={t(`${NS}.startAt`)} value={form.startAt} onChange={(v) => setForm({ ...form, startAt: v })} sx={{ width: '100%' }} />
            <DateField label={t(`${NS}.endAt`)} value={form.endAt} min={form.startAt || undefined} onChange={(v) => setForm({ ...form, endAt: v })} sx={{ width: '100%' }} />
          </Stack>
          <TextField select label={t(`${NS}.appliesTo`)} value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })} helperText={t(`${NS}.appliesToHelp`)} fullWidth>
            <MenuItem value="">{t(`${NS}.allLocations`)}</MenuItem>
            {(locations || []).map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </TextField>
          <TextField label={t(`${NS}.note`)} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} multiline minRows={2} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </FormDialog>
    </Box>
  );
};
